import { useMemo, useRef, useCallback, useEffect } from "react";
import useLeaderboardListCategories from "../hooks/useLeaderboardListCategories";
import useLeaderboardOrdinationStore from "../store/leaderboardOrdinatinoStore";
import "./LeaderboardSelector.css";
import { ASSETS_URL } from "../util/constants";

const SCROLL_AMOUNT = 120;

const LeaderboardSelector = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const setFieldOrder = useLeaderboardOrdinationStore(
    (state) => state.setFieldOrder,
  );
  const currentFieldOrder = useLeaderboardOrdinationStore(
    (state) => state.fieldOrder,
  );
  const { data: medals } = useLeaderboardListCategories();

  // Descobrir a aba ativa lendo o estado global do Zustand
  const activeTab = useMemo(() => {
    if (currentFieldOrder.startsWith("Total_"))
      return currentFieldOrder.replace("Total_", "");
    const activeMedal = medals?.find((m) => m.name === currentFieldOrder);
    return activeMedal?.medalType?.name || "";
  }, [currentFieldOrder, medals]);

  // Filtrar as medalhas para mostrar as da aba selecionada
  const currentTabMedals = useMemo(() => {
    if (!medals || !activeTab) return [];
    return medals.filter((m) => (m.medalType?.name || "Outros") === activeTab);
  }, [medals, activeTab]);

  const getMedalImageUrl = (pathImage: string) => {
    if (!pathImage) return "";
    const cleanPath = pathImage.replace(/^\.\//, "");
    return `${cleanPath}_Platinum.png`;
  };

  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
  }, []);

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [activeTab]);

  if (!activeTab) return null;

  return (
    <div className="medals-selector-container">
      <button
        type="button"
        className="medals-scroll-arrow medals-scroll-arrow-left"
        onClick={scrollLeft}
        aria-label="Rolar para a esquerda"
      >
        ‹
      </button>
      <div ref={scrollRef} className="medals-scroll-wrapper">
        <div className="medals-grid icon-mode">
          <button
            className={`medal-pill overview-pill ${currentFieldOrder === `Total_${activeTab}` ? "active" : ""}`}
            onClick={() => setFieldOrder(`Total_${activeTab}`)}
            title={`Overall from ${activeTab}`}
          >
            <img
              src={`${ASSETS_URL}/badge/UI/All.png`}
              alt="Ranking Geral"
              className="medal-pill-icon"
            />
          </button>
          {currentTabMedals.map((medal) => (
            <button
              key={medal.id}
              className={`medal-pill ${currentFieldOrder === medal.name ? "active" : ""}`}
              onClick={() => setFieldOrder(medal.name)}
              title={`${medal.name}: ${medal.description}`}
            >
              <img
                src={`${ASSETS_URL}/${getMedalImageUrl(medal.pathImage)}`}
                alt={medal.name}
                className="medal-pill-icon"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  if (e.currentTarget.parentElement) {
                    e.currentTarget.parentElement.innerText =
                      medal.name.substring(0, 3);
                  }
                }}
              />
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="medals-scroll-arrow medals-scroll-arrow-right"
        onClick={scrollRight}
        aria-label="Rolar para a direita"
      >
        ›
      </button>
    </div>
  );
};

export default LeaderboardSelector;
